import React from "react";
import { useTranslation } from "react-i18next";
import { Chart, Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { CCard } from "@coreui/react";
import "./ScatterPlot.css";
import { getBloodPressureColor, getBloodPressureStatus } from "../../../../../../config/thresholds";


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

// const ScatterPlot = ({ data }) => {
//   const { t } = useTranslation();

//   const options = {
//     scales: {
//       x: {
//         type: "time",
//         time: {
//           unit: "day",
//         },
//         title: {
//           display: true,
//           text: t("date"),
//         },
//       },
//       y: {
//         title: {
//           display: true,
//           text: t("bloodPressure"),
//         },
//       },
//     },
//     maintainAspectRatio: false,
//     responsive: true,
//   };

//   return (
//     <CCard className="card-graph-scatter">
//       <Scatter data={data} options={options} />
//     </CCard>
//   );
// };

// export default ScatterPlot;

const ScatterPlot = ({ data, thresholdData }) => {
  const { t } = useTranslation();

  // Color constants for health status
  const HEALTH_COLORS = {
    normal: '#22c55e',    // Green
    warning: '#eab308',   // Yellow/Amber
    dangerous: '#ef4444', // Red
    neutral: '#6b7280'    // Gray
  };

  // Function to determine point color based on value and thresholds using thresholds.js
  const getPointColor = (value, isMaxPressure = true) => {
    return getBloodPressureColor(value, isMaxPressure);
  };

  // Function to determine point color based on value and thresholds
  const old_getPointColor = (value, isMaxPressure = true) => {
    if (!thresholdData || thresholdData.lowerThreshold === undefined || thresholdData.upperThreshold === undefined) {
      return HEALTH_COLORS.neutral;
    }

    if (isMaxPressure) {
      // For systolic (max) pressure
      if (value <= 120) return HEALTH_COLORS.normal;
      if (value <= 139) return HEALTH_COLORS.warning;
      return HEALTH_COLORS.dangerous;
    } else {
      // For diastolic (min) pressure
      if (value <= 80) return HEALTH_COLORS.normal;
      if (value <= 89) return HEALTH_COLORS.warning;
      return HEALTH_COLORS.dangerous;
    }
  };

  // Function to create colored datasets
  const createColoredDatasets = (originalData) => {
    if (!originalData || !originalData.datasets) return originalData;

    const newDatasets = originalData.datasets.map((dataset, index) => {
      // Only color the scatter plot datasets, not the threshold lines
      if (dataset.type === 'scatter') {
        let isMaxPressure = false;
        
        // Determine if this is max or min pressure based on label
        if (dataset.label && (
          dataset.label.includes(t("maxBloodPressure")) || 
          dataset.label.includes("Max") ||
          dataset.label.includes("Systolic") ||
          dataset.label.includes("max")
        )) {
          isMaxPressure = true;
        }

        // Create individual point colors and border colors
        const pointColors = dataset.data.map(point => 
          getPointColor(point.y, isMaxPressure)
        );

        return {
          ...dataset,
          backgroundColor: dataset.backgroundColor,
          borderColor: dataset.borderColor,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors.map(color => color),
          // pointRadius: 5, // Make points more visible
          // pointHoverRadius: 7, // Larger on hover
          // pointBorderWidth: 2, // Add border for better visibility
        };
      }
      
      // Return threshold lines and median lines unchanged
      return dataset;
    });

    return {
      ...originalData,
      datasets: newDatasets
    };
  };

  // Process the data to add colors
  const coloredData = createColoredDatasets(data);

  const options = {
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
        },
        title: {
          display: true,
          text: t("date"),
        },
      },
      y: {
        title: {
          display: true,
          text: t("bloodPressure"),
        },
        beginAtZero: false,
        min: 40,        // Set minimum value to 40
        max: 160,       // Set maximum value to 160
        ticks: {
          stepSize: 5,  // Set interval to 5 (40, 45, 50, 55, etc.)
          callback: function(value, index, values) {
            // Ensure we only show ticks at multiples of 5
            return value % 5 === 0 ? value : '';
          }
        }
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: true,
        labels: {
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            const defaultLabels = datasets
            .filter(dataset => dataset.label && dataset.label.trim() !== "") // Filter out empty labels
            .map((dataset, i) => {
              const meta = chart.getDatasetMeta(i);
              const isHidden = meta.hidden;

              // Determine if this is a threshold line
                const isThresholdLine = dataset.label && (
                  dataset.label.includes(t("maxThreshold")) || 
                  dataset.label.includes(t("minThreshold")) ||
                  dataset.label.toLowerCase().includes("threshold")
                );

                // Determine if this is a median line
                const isMedianLine = dataset.label && (
                  dataset.label.includes(t("median")) ||
                  dataset.label.toLowerCase().includes("median")
                );
              
                
              return {
              text: dataset.label,
              fillStyle: Array.isArray(dataset.backgroundColor) 
                ? dataset.backgroundColor[0] 
                : dataset.backgroundColor,
              strokeStyle: Array.isArray(dataset.borderColor)
                ? dataset.borderColor[0]
                : dataset.borderColor,
              datasetIndex: i,
              hidden: isHidden, 
              // Visual styling for hidden items
              fontColor: isHidden ? '#999999' : '#000000', // Gray out hidden items
              lineWidth: isHidden ? 1 : 3, // Thinner line for hidden items
               pointStyle: isThresholdLine ? 'line' : (isMedianLine ? 'rect' : 'line'),
              lineDash: isThresholdLine ? [5, 5] : [], // Dashed line for thresholds
              };
            });

            return defaultLabels;
            // Add health status legend
            // const healthLegend = [
            //   {
            //     text: `${t('normal_status')} (≤120/80)`,
            //     fillStyle: HEALTH_COLORS.normal,
            //     strokeStyle: HEALTH_COLORS.normal,
            //     datasetIndex: null,
            //   },
            //   {
            //     text: `${t('warning_status')} (121-139/81-89)`,
            //     fillStyle: HEALTH_COLORS.warning,
            //     strokeStyle: HEALTH_COLORS.warning,
            //     datasetIndex: null,
            //   },
            //   {
            //     text: `${t('dangerous_status')} (≥140/90)`,
            //     fillStyle: HEALTH_COLORS.dangerous,
            //     strokeStyle: HEALTH_COLORS.dangerous,
            //     datasetIndex: null,
            //   }
            // ];

            // return [...defaultLabels, ...healthLegend];
          },
          usePointStyle: true,
          pointStyle: 'line',
          // filter: (legendItem, chartData) => {
          //   // Show all legend items
          //   return true;
          // },
        },
        onClick: (e, legendItem, legend) => {
          // Fix: Use the legend.chart instead of legendItem.chart
          const chart = legend.chart;
          
          // Check if we have a valid chart and datasetIndex
          if (!chart || legendItem.datasetIndex === null || legendItem.datasetIndex === undefined) {
            return;
          }
          
          try {
            const index = legendItem.datasetIndex;
            const meta = chart.getDatasetMeta(index);
            
            // Toggle visibility
            meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;

            chart.update();

             // Force legend update by regenerating labels
            chart.options.plugins.legend.labels.generateLabels = (chart) => {
              const datasets = chart.data.datasets;
              return datasets
                .filter(dataset => dataset.label && dataset.label.trim() !== "")
                .map((dataset, i) => {
                  const meta = chart.getDatasetMeta(i);
                  const isHidden = meta.hidden;
                  
                  return {
                    text: dataset.label,
                    fillStyle: Array.isArray(dataset.backgroundColor) 
                      ? dataset.backgroundColor[0] 
                      : dataset.backgroundColor,
                    strokeStyle: Array.isArray(dataset.borderColor)
                      ? dataset.borderColor[0]
                      : dataset.borderColor,
                    datasetIndex: i,
                    hidden: isHidden,
                    fontColor: isHidden ? '#999999' : '#000000',
                    lineWidth: isHidden ? 1 : 2,
                  };
                });
            };
            
          } catch (error) {
            console.error('Error toggling dataset visibility:', error);
          }
        },
      },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const value = context.parsed.y;
            const isMaxPressure = context.dataset.label && (
              context.dataset.label.includes(t("maxBloodPressure")) || 
              context.dataset.label.includes("Max") ||
              context.dataset.label.includes("Systolic") ||
              context.dataset.label.includes("max")
            );
            
            const status = getPointColor(value, isMaxPressure);
            let statusText = '';

            switch(status) {
              case 'normal':
                statusText = t('normal_status') || 'Normale';
                break;
              case 'warning':
                statusText = t('warning_status') || 'Attenzione';
                break;
              case 'dangerous':
                statusText = t('dangerous_status') || 'Critico';
                break;
              default:
                statusText = '';
            }
            
            // switch(status) {
            //   case HEALTH_COLORS.normal:
            //     statusText = t('normal_status');
            //     break;
            //   case HEALTH_COLORS.warning:
            //     statusText = t('warning_status');
            //     break;
            //   case HEALTH_COLORS.dangerous:
            //     statusText = t('dangerous_status');
            //     break;
            //   default:
            //     statusText = '';
            // }
            
            return statusText ? `Status: ${statusText}` : '';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'point'
    }
  };

  return (
    <CCard className="card-graph-scatter">
      <Scatter data={coloredData} options={options} />

      {/* Health Status Legend positioned outside the chart
      <div className="health-status-legend">
        <div className="health-status-item">
          <div 
            className="health-status-dot" 
            style={{ backgroundColor: HEALTH_COLORS.normal }}
          ></div>
          <span>{t('normal_status') || 'Normale'} (≤120/80)</span>
        </div>
        <div className="health-status-item">
          <div 
            className="health-status-dot" 
            style={{ backgroundColor: HEALTH_COLORS.warning }}
          ></div>
          <span>{t('warning_status') || 'Attenzione'} (121-130/81-90)</span>
        </div>
        <div className="health-status-item">
          <div 
            className="health-status-dot" 
            style={{ backgroundColor: HEALTH_COLORS.dangerous }}
          ></div>
          <span>{t('dangerous_status') || 'Critico'} (≥131/91)</span>
        </div>
      </div> */}
    </CCard>
  );
};

export default ScatterPlot;